<template>
  <div
    class="flex items-center justify-between space-x-4 overflow-x-auto py-6 pr-4"
    data-cy="generate-link-row"
  >
    <div class="left-col flex shrink items-center">
      <icon-base :icon="data.icon" height="55" width="55" class="mr-4" />
      <div class="flex flex-col">
        <lf-h4>{{ data.title }}</lf-h4>
        <span class="text-xs text-gray-500">{{ data.description }}</span>
      </div>
    </div>
    <div class="right-col flex flex-wrap items-center justify-end space-x-2">
      <div
        class="flex shrink flex-col space-y-4 space-x-0 xl:flex-row xl:space-y-0 xl:space-x-1"
      >
        <template v-if="data.type === APPLICATION_LINK_TYPE_CONTRACT">
          <lf-dropdown
            v-if="data.showSelectWidget"
            :value="selectedWidgetTypeId"
            :placeholder="$t('INTEGRATIONS.WIDGET_NAME')"
            :options="productTypeOptions"
            name="widgetType"
            data-cy="widget-type-dropdown"
            class="min-w-63"
            :teleport-options="{ enabled: true }"
            @change="selectedWidgetTypeId = String($event)"
          />

          <lf-composite-dropdown
            v-model="selectedContractTemplateOption"
            class="max-w-63 min-w-63"
            dropdown-class-names="z-20"
            :placeholder="compositePlaceholder"
            data-cy="contract-template-dropdown"
            :option-sources="
              compact([
                data.showConsentTemplate && {
                  title: t('CUSTOMIZE.CONSENT.TITLE', 2),
                  id: 'consentTemplate',
                  options: (params: GetConsentTemplatesParams) =>
                    consentTemplateService.getAllTemplates({
                      client_id: client?.id,
                      ...params,
                    }),
                },
                {
                  title: showLegacy
                    ? t('DEALS.APPLICATION_LINKS.DOCUSIGN_TEMPLATE')
                    : t('INTEGRATIONS.CONTRACT', 2),
                  id: 'docusignTemplate',
                  options: showLegacy
                    ? legacyContractTemplates
                    : (params: { page: number; search: string }) => {
                        return getContractTemplates({
                          clients: client?.id ? [client?.id] : undefined,
                          status: 2,
                          ...params,
                        });
                      },
                },
              ])
            "
            show-tooltip
          >
            <template #selected="{ value }">
              <span v-if="value.label">
                {{ value.label }}
                <icon-base
                  v-if="isLendflowContractTemplate(value.id)"
                  v-tooltip="
                    `${$t('COMMON.LENDFLOW')} ${$t('COMMON.TEMPLATE')}`
                  "
                  :icon="IconLendflow"
                  view-box="14 12 26 26"
                  class="text-green-500"
                />
              </span>
            </template>
            <template #option="{ option, key }">
              <div class="flex items-center space-x-2">
                <span>{{ option.name || option }}</span>

                <icon-base
                  v-if="isLendflowContractTemplate(key, option)"
                  v-tooltip="
                    `${$t('COMMON.LENDFLOW')} ${$t('COMMON.TEMPLATE')}`
                  "
                  :icon="IconLendflow"
                  view-box="14 12 26 26"
                  class="text-green-500"
                />
              </div>
            </template>
          </lf-composite-dropdown>
        </template>
        <template v-else>
          <div class="flex flex-col space-y-6">
            <lf-dropdown
              v-if="
                data.showBorrowerPlatformTemplate &&
                selectedBorrowerPlatformTemplate
              "
              name="borrower_platform_template_tab"
              class="min-w-63"
              :placeholder="`${t('INTEGRATIONS.BORROWER_PLATFORM.BORROWER_PLATFORM_TEMPLATE')} ${t('COMMON.TAB')}`"
              :value="selectedBorrowerPlatformTemplateTab?.id"
              :options="bpTabDropdownOptions"
              :teleport-options="{ enabled: true }"
              :is-loading="isLoadingTabs"
              @change="handleBorrowerPlatformTemplateTabChange"
            />
            <lf-dropdown-with-api-search
              v-if="data.showBorrowerPlatformTemplate"
              name="borrower_platform_template"
              class="min-w-63"
              full-width
              :placeholder="
                $t('INTEGRATIONS.BORROWER_PLATFORM.BORROWER_PLATFORM_TEMPLATE')
              "
              :value="selectedBorrowerPlatformTemplate?.name || undefined"
              :get-paginated-options="searchBorrowerPlatformTemplates"
              :teleport-options="{ enabled: true }"
              accessor="name"
              sort
              @change="selectedBorrowerPlatformTemplate = $event"
            />
            <lf-dropdown
              v-if="data.showExpiration"
              :value="selectedBpLinkExpirationOption"
              :placeholder="`${t('COMMON.LINK')} ${t('DEALS.LIENS.EXPIRATION')}`"
              :options="expirationDayOptions"
              name="expirationDayDropdown"
              data-cy="expiration-days-dropdown"
              class="min-w-63"
              :teleport-options="{ enabled: true }"
              @change="selectedBpLinkExpirationOption = Number($event)"
            />
            <lf-input
              v-if="selectedBpLinkExpirationOption == 100"
              :placeholder="`${t('COMMON.CUSTOM')} ${t('COMMON.DAYS')} (1-30)`"
              :value="bpLinkExpirationDays"
              type="number"
              min="1"
              max="30"
              name="expirationDay"
              data-cy="expiration-days-input"
              @change="bpLinkExpirationDays = $event.target.value"
            />
          </div>
        </template>
      </div>
      <lf-link
        data-cy="generate-link-button"
        :underlined="false"
        class="flex shrink-0 grow items-center justify-end"
        @click="generateLink"
      >
        <icon-base icon="link-small" class="mr-1" />
        {{ $t("COMMON.GENERATE_LINK") }}
      </lf-link>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import compact from "lodash/compact";
import pick from "lodash/pick";

import { AnalyticsEvent } from "@/enums/analytics";
import {
  APPLICATION_LINK_TYPE_CONTRACT,
  APPLICATION_SOURCE,
  DEFAULT_EXPIRATION_DAYS,
} from "@/helpers/constants";
import { useIntegration } from "@/hooks/auth";
import { useClipboardWithMessage } from "@/hooks/clipboard";
import { usePromiseWrapper } from "@/hooks/common";
import { useContractBuilder } from "@/hooks/contractBuilder";
import { useDeals } from "@/hooks/deals";
import { useBorrowerPlatformTemplateQuery } from "@/hooks/queries/useBorrowerPlatformTemplates";
import {
  type BorrowerPlatformTemplate,
  type Tab,
  TabType,
  TabVisibility,
} from "@/models/borrowerPlatformTemplates";
import applicationsApiService from "@/services/modules/applications";
import borrowerPlatformTemplateService from "@/services/modules/borrowerPlatformTemplates";
import consentTemplateService, {
  type GetConsentTemplatesParams,
} from "@/services/modules/consentTemplate";
import { useAnalyticsStore } from "@/stores/analytics";
import { useContractBuilderStore } from "@/stores/contractBuilder";

import IconLendflow from "@/components/icons/IconLendflow.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfLink from "@/components/ui/LfLink.vue";
import LfCompositeDropdown from "@/components/ui/inputs/LfCompositeDropdown.vue";
import LfDropdown from "@/components/ui/inputs/LfDropdown.vue";
import LfDropdownWithApiSearch from "@/components/ui/inputs/LfDropdownWithApiSearch.vue";

import type { IApplication } from "@/models/applications";
import type {
  CompositeDropdownModel,
  IGenerateLinkData,
} from "@/models/common";

const RESUME_SUPPORTED_BUSINESS_CREDIT_WIDGETS = [4, 8, 26];
const RESUME_SUPPORTED_LENDING_WIDGETS = [1, 9, 10, 18, 19, 27, 30, 32];

const {
  data,
  successText = "",
  showLegacy = false,
  applicationData,
} = defineProps<{
  data: IGenerateLinkData;
  successText?: string;
  showLegacy?: boolean;
  applicationData: IApplication;
}>();

const contractsStore = useContractBuilderStore();
const { trackEvent } = useAnalyticsStore();

const { state, dispatch } = useStore();
const { t } = useI18n();
const { isLendflowContractTemplate } = useContractBuilder();
const copyToClipboard = useClipboardWithMessage();
const { getFilteredProductTypes } = useIntegration();
const { activeDeal } = useDeals();
const { getContractTemplates } = contractsStore;

const selectedContractTemplateOption = ref<CompositeDropdownModel>({
  id: "",
  label: "",
  source: "",
});

const selectedWidgetTypeId = ref(
  applicationData.is_business_credit ? "4" : "1"
);

const expirationDayOptions = {
  7: `7 ${t("COMMON.DAYS")}`,
  30: `30 ${t("COMMON.DAYS")}`,
  100: t("COMMON.CUSTOM"),
};

const selectedBpLinkExpirationOption = ref<number>(DEFAULT_EXPIRATION_DAYS);
const bpLinkExpirationDays = ref<number>(DEFAULT_EXPIRATION_DAYS);
const selectedBorrowerPlatformTemplate = ref<BorrowerPlatformTemplate | null>(
  null
);
const selectedBorrowerPlatformTemplateTab = ref<Tab | null>(null);

const selectedTemplateId = computed(
  () => selectedBorrowerPlatformTemplate.value?.id ?? ""
);
const { data: bpTemplateData, isLoading: isLoadingTabs } =
  useBorrowerPlatformTemplateQuery(selectedTemplateId);

const filteredTabs = computed(() => {
  if (!bpTemplateData.value?.content?.tabs) {
    return [];
  }

  return bpTemplateData.value.content.tabs.filter((tab) => {
    if (
      tab.type === TabType.WORKFLOW &&
      tab.workflow_template_ids?.length &&
      activeDeal.value.workflow_template_id &&
      !tab.workflow_template_ids.includes(activeDeal.value.workflow_template_id)
    ) {
      return false;
    }
    if (tab.type === TabType.LOG_OUT) {
      return false;
    }
    if (
      tab.visibility === TabVisibility.ALWAYS ||
      tab.visibility === TabVisibility.AUTHENTICATED
    ) {
      return true;
    }
    return false;
  });
});

const bpTabDropdownOptions = computed(() => {
  return filteredTabs.value.reduce<Record<string, string>>((acc, tab) => {
    acc[tab.id] = tab.title ?? "";
    return acc;
  }, {});
});

const legacyContractTemplates = computed(() => {
  return state.auth.authClientIntegration?.docusign?.placeholders || {};
});

const client = computed(() => applicationData.client);

const compositePlaceholder = computed(() =>
  data.showConsentTemplate
    ? t("CUSTOMIZE.CONSENT.TITLE")
    : showLegacy
      ? t("DEALS.APPLICATION_LINKS.DOCUSIGN_TEMPLATE")
      : t("INTEGRATIONS.CONTRACT", 2)
);

const productTypeOptions = computed(() => {
  const widgetList = applicationData.is_business_credit
    ? RESUME_SUPPORTED_BUSINESS_CREDIT_WIDGETS
    : RESUME_SUPPORTED_LENDING_WIDGETS;
  const availabeProductTypes = getFilteredProductTypes({ includeNone: false });

  return pick(availabeProductTypes, widgetList);
});

const { fetchWrapper: searchBorrowerPlatformTemplates } = usePromiseWrapper(
  async (params: { page: number; search: string }) => {
    const payload: Record<string, number | string | undefined> = {
      client_id: client.value?.id,
      page: params.page,
    };

    if (params.search?.trim()) {
      payload.search = params.search;
    }

    return await borrowerPlatformTemplateService.getBorrowerPlatformTemplates({
      payload,
    });
  }
);

const handleBorrowerPlatformTemplateTabChange = (tabId: string | number) => {
  const tab = filteredTabs.value.find((t) => t.id === String(tabId));
  selectedBorrowerPlatformTemplateTab.value = tab ?? null;
};

const generateLink = async () => {
  const payload = {
    ...data.linkData,
  };
  if (data.type === APPLICATION_LINK_TYPE_CONTRACT) {
    if (
      selectedContractTemplateOption.value.id &&
      selectedContractTemplateOption.value.id !== "0"
    ) {
      let targetKey = "consent_template_id";

      if (selectedContractTemplateOption.value.source === "docusignTemplate") {
        targetKey = showLegacy
          ? "docusign_placeholder_id"
          : "contract_template_id";
      }
      payload[targetKey] = selectedContractTemplateOption.value.id;
    }
  }
  if (data.showSelectWidget) {
    payload.product = selectedWidgetTypeId.value;
  }

  if (Number(selectedBpLinkExpirationOption.value) !== 100) {
    bpLinkExpirationDays.value = selectedBpLinkExpirationOption.value;
  }

  if (data.showExpiration) {
    payload.expirationDays = bpLinkExpirationDays.value;
  }

  if (
    data.showBorrowerPlatformTemplate &&
    selectedBorrowerPlatformTemplate.value
  ) {
    payload.borrower_platform_template_id =
      selectedBorrowerPlatformTemplate.value.id;
  }

  if (
    data.showBorrowerPlatformTemplate &&
    selectedBorrowerPlatformTemplate.value &&
    selectedBorrowerPlatformTemplateTab.value
  ) {
    payload.borrower_platform_template_tab_id =
      selectedBorrowerPlatformTemplateTab.value.id;
  }

  const link = await dispatch(data.action, payload);
  await copyToClipboard(link, { success: successText });

  if (link) {
    trackEvent(AnalyticsEvent.DealAppLinkGenerated, {
      link_type: data.type,
    });
  }
};

watch(selectedBorrowerPlatformTemplate, () => {
  selectedBorrowerPlatformTemplateTab.value = null;
});

onMounted(async () => {
  if (activeDeal.value.source !== APPLICATION_SOURCE.WIDGET) {
    return;
  }
  const widgetInfo = await applicationsApiService.getWidgetInfo(
    activeDeal.value.id
  );

  if (widgetInfo.consent_template) {
    selectedContractTemplateOption.value = {
      id: widgetInfo.consent_template.id,
      label: widgetInfo.consent_template.name,
      source: "consentTemplate",
    };
  } else if (widgetInfo.contract_template) {
    selectedContractTemplateOption.value = {
      id: widgetInfo.contract_template.id,
      label: widgetInfo.contract_template.name,
      source: "docusignTemplate",
    };
  } else {
    const { data: consentTemplates } =
      await consentTemplateService.getAllTemplates({
        client_id: client.value?.id,
      });
    if (!consentTemplates.length) {
      return;
    }
    const { name, id } = consentTemplates[0];
    selectedContractTemplateOption.value = {
      id,
      label: name,
      source: "consentTemplate",
    };
  }
});
</script>
